import React, { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { useLang } from "@/context/LangContext";

const I18N = {
  sk: {
    label: "Stiahnuť app", title: "Stiahnuť aplikáciu do mobilu", hintTitle: "Inštalácia Tigik na iPhone",
    step1: "Otvor Tigik v aplikácii Safari",
    step2: "Dolu klikni na ikonu",
    step3Pre: "V menu vyber",
    step3Bold: "„Pridať na plochu",
    step4: "Hotovo — Tigik sa otvára ako natívna aplikácia. 📱",
    step3Bold2: "„Pridať",
    step3Post: "(vpravo hore).",
    androidTitle: "Inštalácia Tigik na Android",
    androidStep1: "Otvor Tigik v Chrome",
    androidStep2: "Klikni na tri bodky vpravo hore ⋮",
    androidStep3: "Vyber „Pridať na plochu\" alebo „Install app\"",
    ok: "OK, rozumiem", shareLabel: "Share / Zdieľať",
  },
  en: {
    label: "Install app", title: "Install app on mobile", hintTitle: "Install Tigik on iPhone",
    step1: "Open Tigik in the Safari app",
    step2: "At the bottom, tap the icon",
    step3Pre: "From the menu, choose",
    step3Bold: "„Add to Home Screen",
    step4: "Done — Tigik now opens like a native app. 📱",
    step3Bold2: "„Add",
    step3Post: "(top right).",
    androidTitle: "Install Tigik on Android",
    androidStep1: "Open Tigik in Chrome",
    androidStep2: "Tap the three dots ⋮ (top right)",
    androidStep3: "Choose „Add to Home screen\" or „Install app\"",
    ok: "OK, got it", shareLabel: "Share",
  },
  cs: {
    label: "Stáhnout app", title: "Stáhnout aplikaci do mobilu", hintTitle: "Instalace Tigik na iPhone",
    step1: "Otevři Tigik v aplikaci Safari",
    step2: "Dole klikni na ikonu",
    step3Pre: "V menu vyber",
    step3Bold: "„Přidat na plochu",
    step4: "Hotovo — Tigik se otevírá jako nativní aplikace. 📱",
    step3Bold2: "„Přidat",
    step3Post: "(vpravo nahoře).",
    androidTitle: "Instalace Tigik na Android",
    androidStep1: "Otevři Tigik v Chrome",
    androidStep2: "Klikni na tři tečky ⋮ vpravo nahoře",
    androidStep3: "Vyber „Přidat na plochu\" nebo „Instalovat app\"",
    ok: "OK, rozumím", shareLabel: "Sdílet",
  },
  de: {
    label: "App laden", title: "App auf Mobilgerät laden", hintTitle: "Tigik auf iPhone installieren",
    step1: "Öffne Tigik in der Safari-App",
    step2: "Tippe unten auf das Symbol",
    step3Pre: "Wähle im Menü",
    step3Bold: "„Zum Home-Bildschirm",
    step4: "Fertig — Tigik öffnet sich jetzt wie eine native App. 📱",
    step3Bold2: "„Hinzufügen",
    step3Post: "(oben rechts).",
    androidTitle: "Tigik auf Android installieren",
    androidStep1: "Öffne Tigik in Chrome",
    androidStep2: "Tippe auf die drei Punkte ⋮ (oben rechts)",
    androidStep3: "Wähle „Zum Startbildschirm\" oder „App installieren\"",
    ok: "OK, verstanden", shareLabel: "Teilen",
  },
  pl: {
    label: "Pobierz app", title: "Pobierz aplikację na telefon", hintTitle: "Instalacja Tigik na iPhone",
    step1: "Otwórz Tigik w aplikacji Safari",
    step2: "Na dole kliknij ikonę",
    step3Pre: "W menu wybierz",
    step3Bold: "„Dodaj do ekranu początkowego",
    step4: "Gotowe — Tigik otwiera się jak natywna aplikacja. 📱",
    step3Bold2: "„Dodaj",
    step3Post: "(prawy górny róg).",
    androidTitle: "Instalacja Tigik na Android",
    androidStep1: "Otwórz Tigik w Chrome",
    androidStep2: "Kliknij trzy kropki ⋮ (prawy górny róg)",
    androidStep3: "Wybierz „Dodaj do ekranu\" lub „Zainstaluj aplikację\"",
    ok: "OK, rozumiem", shareLabel: "Udostępnij",
  },
  fr: {
    label: "Télécharger l'app", 
    title: "Télécharger l'application sur mobile", 
    hintTitle: "Installer Tigik sur iPhone",
    step1: "Ouvre Tigik dans l'app Safari",
    step2: "En bas, clique sur l'icône",
    step3Pre: "Dans le menu, choisis",
    step3Bold: "« Ajouter à l'écran d'accueil »",
    step4: "C'est fait — Tigik s'ouvre comme une app native. 📱",
    step3Bold2: "« Ajouter »",
    step3Post: "(en haut à droite).",
    androidTitle: "Installer Tigik sur Android",
    androidStep1: "Ouvre Tigik dans Chrome",
    androidStep2: "Clique sur les trois points en haut à droite ⋮",
    androidStep3: "Choisis « Ajouter à l'écran d'accueil » ou « Install app »",
    ok: "OK, j'ai compris", 
    shareLabel: "Share / Partager"
},
  ru: { label: "Скачать", title: "Скачать приложение на телефон", hintTitle: "Установка Tigik на iPhone",
    step1: "Открой Tigik в Safari", step2: "Внизу нажми на иконку",
    step3Pre: "В меню выбери", step3Bold: "„На экран «Домой»",
    step4: "Готово — Tigik открывается как нативное приложение. 📱",
    step3Bold2: "„Добавить", step3Post: "(справа сверху).",
    androidTitle: "Установка Tigik на Android",
    androidStep1: "Открой Tigik в Chrome",
    androidStep2: "Нажми три точки ⋮ (справа сверху)",
    androidStep3: "Выбери „Добавить на главный\" или „Установить\"",
    ok: "OK", shareLabel: "Поделиться" },
  uk: { label: "Завантажити", title: "Завантажити застосунок", hintTitle: "Встановлення Tigik на iPhone",
    step1: "Відкрий Tigik у Safari", step2: "Внизу натисни на іконку",
    step3Pre: "У меню обери", step3Bold: "„На екран «Домівка»",
    step4: "Готово — Tigik відкривається як нативний застосунок. 📱",
    step3Bold2: "„Додати", step3Post: "(справа зверху).",
    androidTitle: "Встановлення Tigik на Android",
    androidStep1: "Відкрий Tigik у Chrome",
    androidStep2: "Натисни три крапки ⋮ (справа зверху)",
    androidStep3: "Обери „Додати на екран\" або „Встановити\"",
    ok: "OK", shareLabel: "Поділитися" },
};

/**
 * PWA install button (Tigik). Captures `beforeinstallprompt` and shows a button.
 * On iOS (no event), shows an "Add to Home Screen" hint instead.
 */
export default function InstallAppButton({ className = "", big = false }) {
  const { lang } = useLang();
  const i = I18N[lang] || I18N.en;
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  const isIos = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => setInstalled(true));
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const onClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      return;
    }
    setShowIosHint(true);
  };

  if (installed) return null;

  return (
    <>
      <button
        data-testid="install-app-btn"
        onClick={onClick}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-gold-500/10 border border-gold-600 hover:bg-gold-500/20 hover:border-gold-400 text-gold-400 hover:text-gold-200 transition-all ${className}`}
        title={i.title}
      >
        <Download className="w-3.5 h-3.5" />
        {big ? <span>{i.title}</span> : <>
          <span className="hidden sm:inline">{i.label}</span>
          <span className="sm:hidden">App</span>
        </>}
      </button>
      {showIosHint && (
        <div
          data-testid="install-hint-modal"
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowIosHint(false)}
        >
          <div
            className="bg-[var(--wb-surface)] border border-gold-700/50 p-6 max-w-md w-full shadow-[0_20px_60px_-10px_rgba(212,175,55,0.45)] relative"
            onClick={(e) => e.stopPropagation()}
            style={{ paddingBottom: "max(env(safe-area-inset-bottom), 24px)" }}
          >
            <button
              type="button"
              onClick={() => setShowIosHint(false)}
              className="absolute top-3 right-3 text-zinc-500 hover:text-white p-1"
              aria-label="Close"
            >
              ✕
            </button>
            <h3 className="font-display font-bold text-lg mb-1">{isIos ? i.hintTitle : i.androidTitle}</h3>
            <p className="text-[11px] font-mono uppercase tracking-wider text-gold-500 mb-4">
              {isIos ? "iPhone · Safari" : "Android · Chrome"}
            </p>
            {isIos ? (
              <ol className="text-sm text-zinc-300 space-y-3">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 border border-gold-600 text-gold-400 flex items-center justify-center text-xs font-mono">1</span>
                  <span>{i.step1}</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 border border-gold-600 text-gold-400 flex items-center justify-center text-xs font-mono">2</span>
                  <span className="flex-1">
                    {i.step2} <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#007AFF]/15 border border-[#007AFF]/40 rounded text-[11px] text-[#5AA3FF] font-mono mx-0.5">
                      <svg viewBox="0 0 16 16" className="w-3 h-3 inline" fill="currentColor"><path d="M8 1.5L11 4.5h-2v6h-2v-6h-2L8 1.5zM3 7v6.5C3 14.33 3.67 15 4.5 15h7c.83 0 1.5-.67 1.5-1.5V7h-1.5v6.5h-7V7H3z"/></svg>
                      {i.shareLabel}
                    </span>
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 border border-gold-600 text-gold-400 flex items-center justify-center text-xs font-mono">3</span>
                  <span>{i.step3Pre} <strong className="text-white">{i.step3Bold}"</strong></span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 border border-gold-600 text-gold-400 flex items-center justify-center text-xs font-mono">4</span>
                  <span>{i.step4}</span>
                </li>
              </ol>
            ) : (
              <ol className="text-sm text-zinc-300 space-y-3">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 border border-gold-600 text-gold-400 flex items-center justify-center text-xs font-mono">1</span>
                  <span>{i.androidStep1}</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 border border-gold-600 text-gold-400 flex items-center justify-center text-xs font-mono">2</span>
                  <span>{i.androidStep2}</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 border border-gold-600 text-gold-400 flex items-center justify-center text-xs font-mono">3</span>
                  <span>{i.androidStep3}</span>
                </li>
              </ol>
            )}
            <button
              onClick={() => setShowIosHint(false)}
              data-testid="install-hint-ok"
              className="wb-btn-primary w-full py-2.5 mt-6 text-sm"
            >
              {i.ok}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
